import React, { useState } from 'react';
import { useCompanyStore } from '../../../stores/companyStore';
import { notify } from '../../../stores/notificationStore';
import { confirm } from '../../../stores/confirmStore';
import {
  isVatExempt,
  isIntraCommunity,
  resolveDocumentVatExemptArticle,
  formatDate,
} from '../../../utils/vatCalculations';
import { InvoiceTemplateProps, InvoiceItem } from '../types';
import { ActionBar } from '../shared/ActionBar';
import { TotalsSection } from '../shared/TotalsSection';
import { LegalFooter } from '../shared/LegalFooter';
import { ItemsTable } from '../shared/ItemsTable';
import { UnifiedEmailModal, EmailData } from '../../modals/UnifiedEmailModal';
import { PaymentSchedulesManagement } from '../../../features/payments';
import { publicLinksAPI, invoicesAPI } from '../../../utils/api';
import { logger } from '../../../utils/logger';

/**
 * Template Modern - Design Your Brand (STARTER)
 * Design exact du PDF fourni avec couleurs pastel
 * - Header: "Facture" gris à gauche, "Design your BRAND" orange à droite
 * - Layout 2 colonnes pour De:/Pour:
 * - Table avec header orange pastel (#fed7aa)
 * - Conditions de paiement en 2 colonnes
 */
export const InvoiceTemplateModern: React.FC<InvoiceTemplateProps> = ({
  invoiceData,
  onClose,
  template,
  company: companyProp,
  color: colorProp
}) => {
  const { company: companyStore, invoiceColor: invoiceColorStore } = useCompanyStore();
  const [isLoading, setIsLoading] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showPaymentSchedules, setShowPaymentSchedules] = useState(false);
  const [publicLink, setPublicLink] = useState<string>('');

  const company = companyProp || companyStore;
  const invoiceColor = colorProp || invoiceColorStore || template?.primaryColor || '#FED7AA';

  const clientCountryCode = (invoiceData as any)?.Client?.countryCode;
  const isExempt = isVatExempt(company?.vatRegime) || isIntraCommunity(clientCountryCode, parseFloat(String(invoiceData?.totalTVA)) || undefined);
  const vatExemptArticle = resolveDocumentVatExemptArticle(company?.vatRegime, clientCountryCode, parseFloat(String(invoiceData?.totalTVA)) || undefined);

  if (!invoiceData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-6 max-w-md text-center">
          <p className="text-red-600 font-semibold mb-4">Erreur : Aucune donnée de facture</p>
          <button onClick={onClose} className="px-4 py-2 bg-orange-200 hover:bg-orange-300 text-gray-800 rounded-lg transition-all">
            Retour
          </button>
        </div>
      </div>
    );
  }

  const items: InvoiceItem[] = typeof invoiceData.items === 'string'
    ? JSON.parse(invoiceData.items).map((item: any) => {
        const unitPrice = Number(item.unitPrice) || 0;
        const quantity = Number(item.quantity) || 0;
        return {
          ...item,
          unitPrice,
          quantity,
          total: Number(item.totalHT) || Number(item.total) || (unitPrice * quantity),  // ✅ Fallback vers calcul
          description: item.description || '',
          vatRate: item.taxPercent || (isExempt ? 0 : 20),  // ✅ Prisma utilise taxPercent
          vatAmount: item.vatAmount || 0,
        };
      })
    : (invoiceData.items || []).map((item: any) => {
        const unitPrice = Number(item.unitPrice) || 0;
        const quantity = Number(item.quantity) || 0;
        return {
          ...item,
          unitPrice,
          quantity,
          total: Number(item.totalHT) || Number(item.total) || (unitPrice * quantity),  // ✅ Fallback vers calcul
          description: item.description || '',
          vatRate: item.taxPercent || (isExempt ? 0 : 20),  // ✅ Prisma utilise taxPercent
          vatAmount: item.vatAmount || 0,
        };
      });

  const discountType = invoiceData.discountType || 'none';
  const discountValue = parseFloat(String(invoiceData.discountValue)) || 0;
  const discountAmount = parseFloat(String(invoiceData.discountAmount)) || 0;

  // ✅ Construire la ventilation TVA à partir des items (affichage uniquement)
  const vatBreakdown: Array<{ rate: number; baseHT: number; vatAmount: number }> = [];
  const vatByRate: Record<number, { baseHT: number; vatAmount: number }> = {};

  items.forEach((item: InvoiceItem) => {
    const rate = item.vatRate || 0;
    if (!vatByRate[rate]) {
      vatByRate[rate] = { baseHT: 0, vatAmount: 0 };
    }
    vatByRate[rate].baseHT += item.total || 0;
    vatByRate[rate].vatAmount += item.vatAmount || 0;
  });

  Object.entries(vatByRate).forEach(([rate, data]) => {
    vatBreakdown.push({
      rate: Number(rate),
      baseHT: data.baseHT,
      vatAmount: data.vatAmount
    });
  });

  // ✅ Utiliser les valeurs du backend (source de vérité)
  const subtotal = parseFloat(String(invoiceData.totalHT)) || parseFloat(String(invoiceData.subtotal)) || 0;
  const tax = parseFloat(String(invoiceData.totalTVA)) || parseFloat(String(invoiceData.tax)) || 0;
  const total = parseFloat(String(invoiceData.totalTTC)) || parseFloat(String(invoiceData.total)) || 0;

  const handleGeneratePDF = async () => {
    if (!invoiceData.id) {
      notify.error('ID de la facture manquant', 'Génération PDF');
      return;
    }
    setIsLoading(true);
    try {
      // ✅ Utilisation du backend pour générer le PDF (templates uniformisés)
      const blob = await invoicesAPI.downloadPDF(invoiceData.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const clientName = (invoiceData as any).Client?.fullName || (invoiceData as any).Client?.companyName || 'Client';
      a.download = `Facture_${invoiceData.invoiceNumber}_${clientName.replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      notify.success('PDF généré avec succès', 'Génération PDF');
    } catch (error) {
      logger.error('Erreur génération PDF:', error);
      notify.error("Erreur lors de la génération du PDF : " + (error as Error).message, 'Génération PDF');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateFacturX = async () => {
    if (!invoiceData.id) {
      notify.error('ID de la facture manquant', 'Factur-X');
      return;
    }
    setIsLoading(true);
    try {
      // ✅ Utilisation du backend pour générer le PDF Factur-X (templates uniformisés)
      const blob = await invoicesAPI.downloadFacturXPDF(invoiceData.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const clientName = (invoiceData as any).Client?.fullName || (invoiceData as any).Client?.companyName || 'Client';
      a.download = `Facture_FacturX_${invoiceData.invoiceNumber}_${clientName.replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      notify.success('PDF Factur-X généré avec succès', 'Factur-X');
    } catch (error) {
      logger.error('Erreur Factur-X:', error);
      notify.error("Erreur lors de la génération du PDF Factur-X : " + (error as Error).message, 'Erreur Factur-X');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleOpenEmailModal = async () => {
    try {
      setIsLoading(true);
      // ✅ Utilisation de l'API Layer
      if (!invoiceData.id) {
        throw new Error('ID de la facture manquant');
      }
      const result = await publicLinksAPI.getInvoiceLink(invoiceData.id);
      if (result.success && result.data && result.data.length > 0) {
        const activeLink = result.data.find((link: any) => link.isActive);
        if (activeLink) {
          setPublicLink(activeLink.url);
        }
      }
    } catch (error) {
      logger.error('Erreur lors de la récupération du lien public:', error);
      // Continue quand même, le lien sera vide
    } finally {
      setIsLoading(false);
      setShowEmailModal(true);
    }
  };

  const handleSendEmail = async (emailData: EmailData) => {
    try {
      if (!invoiceData?.id) {
        throw new Error('ID de la facture manquant');
      }

      // Générer le PDF Factur-X de la facture
      setIsLoading(true);
      const { generateFacturXPDF, convertInvoiceToFacturX } = await import('../../../utils/facturx');
      const facturxData = convertInvoiceToFacturX({ ...invoiceData, items: items }, company);
      const pdfBlob = await generateFacturXPDF(facturxData);

      // Préparer FormData pour l'envoi
      const formData = new FormData();
      formData.append('to', emailData.to);
      formData.append('subject', emailData.subject);
      formData.append('body', emailData.body);
      formData.append('pdf', pdfBlob, `Facture_${invoiceData.invoiceNumber}.pdf`);

      // ✅ Utilisation de l'API Layer
      await publicLinksAPI.sendInvoiceEmail(invoiceData.id, formData);

      notify.success(`Email envoyé à ${emailData.to}`, 'Envoi réussi');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erreur inconnue';
      notify.error(message, 'Erreur d\'envoi');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen print:min-h-0 print:bg-white">
      <ActionBar
        onGeneratePDF={handleGeneratePDF}
        onGenerateFacturX={handleGenerateFacturX}
        onSendEmail={handleOpenEmailModal}
        onPrint={handlePrint}
        onClose={onClose}
        onPaymentSchedules={() => setShowPaymentSchedules(true)}
        isLoading={isLoading}
        documentType="invoice"
        invoiceStatus={invoiceData.status}
      />

      {/* Payment Schedules Modal */}
      {showPaymentSchedules && invoiceData.id && (
        <PaymentSchedulesManagement
          invoiceId={invoiceData.id}
          invoiceNumber={invoiceData.invoiceNumber}
          invoiceTotal={total}
          onClose={() => setShowPaymentSchedules(false)}
        />
      )}

      <UnifiedEmailModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        onSend={handleSendEmail}
        documentType="invoice"
        documentNumber={invoiceData.invoiceNumber}
        clientEmail={(invoiceData as any).Client?.email || ''}
        clientName={(invoiceData as any).Client?.fullName || (invoiceData as any).Client?.companyName || ''}
        showPdfPreview={false}
        templateVariables={{
          invoice_number: invoiceData.invoiceNumber || '',
          client_name: (invoiceData as any).Client?.fullName || (invoiceData as any).Client?.companyName || '',
          total: total.toFixed(2),
          public_link: publicLink,
          due_date: formatDate(invoiceData.dueDate || invoiceData.issueDate),
          payment_conditions: company?.defaultPaymentTermsDays ? `Paiement à ${company.defaultPaymentTermsDays} jours` : 'Paiement à 30 jours',
          iban: company?.iban || '',
          bic: company?.bic || '',
          companyName: company?.legalName || company?.tradeName || '',
          companyEmail: company?.email || '',
          companyPhone: company?.phone || '',
        }}
      />

      <div className="py-8 print:py-0 print:mt-0">
        <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-2xl print:shadow-none print:max-w-full print:rounded-none p-12 print:p-8" id="invoice-template">

          {/* Header avec Facture et Design Your Brand */}
          <div className="flex justify-between items-start mb-12 pb-8 border-b-2 border-gray-200">
            <div>
              <h1 className="text-3xl font-bold" style={{ color: invoiceColor }}>Facture</h1>
              <div className="text-xs text-gray-600 mt-2">
                N° <strong className="text-gray-800">{invoiceData.invoiceNumber}</strong>
              </div>
            </div>
            <div className="text-right">
              {company?.logoUrl && (
                <img
                  src={company.logoUrl}
                  alt={company.legalName || company.tradeName || 'Logo entreprise'}
                  className="h-16 md:h-20 max-w-[200px] object-contain ml-auto"
                />
              )}
            </div>
          </div>

          {/* Addresses Section - 2 columns */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-12">
            {/* From Section - Émetteur */}
            <div>
              <p className="text-sm font-bold text-gray-800 mb-1">
                {company?.legalName || company?.tradeName}
              </p>
              {company?.addressLine1 && <p className="text-sm text-gray-800">{company.addressLine1}</p>}
              {(company?.zipCode || company?.city) && (
                <p className="text-sm text-gray-800">
                  {company.zipCode} {company.city}
                </p>
              )}
              {company?.phone && <p className="text-sm text-gray-800">{company.phone}</p>}
              {company?.email && <p className="text-sm text-gray-800">{company.email}</p>}
            </div>

            {/* To Section - Client */}
            <div>
              {/* Utilise UNIQUEMENT la structure Prisma V2 */}
              {((invoiceData as any).Client?.companyName || (invoiceData as any).Client?.fullName) && (
                <p className="text-sm font-bold text-gray-800 mb-1">
                  {(invoiceData as any).Client?.companyName || (invoiceData as any).Client?.fullName}
                </p>
              )}
              {(invoiceData as any).Client?.fullName && (invoiceData as any).Client?.companyName && (
                <p className="text-sm text-gray-800">{(invoiceData as any).Client?.fullName}</p>
              )}
              {(invoiceData as any).Client?.addressLine1 && (
                <p className="text-sm text-gray-800">{(invoiceData as any).Client?.addressLine1}</p>
              )}
              {((invoiceData as any).Client?.zipCode || (invoiceData as any).Client?.city) && (
                <p className="text-sm text-gray-800">
                  {(invoiceData as any).Client?.zipCode} {(invoiceData as any).Client?.city}
                </p>
              )}
              {(invoiceData as any).Client?.phone && (
                <p className="text-sm text-gray-800 mt-2">
                  <span className="text-gray-600">Tél: </span>
                  {(invoiceData as any).Client?.phone}
                </p>
              )}
              {(invoiceData as any).Client?.email && (
                <p className="text-sm text-gray-800">
                  <span className="text-gray-600">Email: </span>
                  {(invoiceData as any).Client?.email}
                </p>
              )}
            </div>
          </div>

          {/* Dates Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-12 pb-8 border-b border-gray-200">
            <div>
              <p className="text-sm font-semibold text-gray-600 mb-1">Date d'émission</p>
              <p className="text-sm font-semibold text-gray-800">{formatDate(invoiceData.issueDate)}</p>
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-600 mb-1">Date d'exigibilité du paiement</p>
              <p className="text-sm font-semibold text-gray-800">{formatDate(invoiceData.dueDate)}</p>
            </div>
          </div>

          {/* Titre du document */}
          {invoiceData.title && (
            <div
              className="mb-8 text-2xl font-bold pb-2 border-b-2 text-gray-800"
              style={{
                borderImage: `linear-gradient(135deg, ${invoiceColor} 0%, #14b8a6 100%) 1`
              }}
            >
              {invoiceData.title}
            </div>
          )}

          {/* TABLEAU DES ITEMS — délégué à ItemsTable (sections, remises, descriptions BTP) */}
          <ItemsTable
            items={items}
            tableStyle="minimal"
            accentColor={invoiceColor}
            showVat={!isExempt}
          />

          {/* Totals Section - Composant partagé */}
          <TotalsSection
            subtotal={subtotal}
            tax={tax}
            total={total}
            discount={discountAmount}
            discountType={discountType}
            discountValue={discountValue}
            vatBreakdown={vatBreakdown}
            accentColor={invoiceColor}
            showVatBreakdown={!isExempt}
            isVatExempt={isExempt}
            vatExemptionMention={vatExemptArticle}
          />

          {/* Notes Internes (affichées sur le document) */}
          {invoiceData.internalNotes && (
            <div className="mt-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 pb-2 border-b-2" style={{ borderColor: invoiceColor }}>
                Notes
              </h3>
              <p className="text-gray-700 p-4 rounded-lg text-sm whitespace-pre-wrap" style={{ backgroundColor: invoiceColor + '15' }}>
                {invoiceData.internalNotes}
              </p>
            </div>
          )}

          {/* Mentions particulières */}
          {invoiceData.termsConditions && (
            <div className="mt-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 pb-2 border-b-2" style={{ borderColor: invoiceColor }}>
                Mentions particulières
              </h3>
              <p className="text-gray-700 p-4 rounded-lg text-sm whitespace-pre-wrap" style={{ backgroundColor: invoiceColor + '15' }}>
                {invoiceData.termsConditions}
              </p>
            </div>
          )}

          {/* Legal Footer */}
          <LegalFooter company={company} notes={invoiceData.notes} vatExemptArticle={vatExemptArticle} />

        </div>
      </div>
    </div>
  );
};
